// src/composables/useChunkRenderer.ts
import { ref, watch, nextTick, type Ref } from 'vue'
import { debounce } from 'lodash'

export function useChunkRenderer<T>(source: Ref<T[]>, options: { chunkSize?: number; delay?: number } = {}) {
  const { chunkSize = 100, delay = 200 } = options

  const chunkedItems = ref<T[]>([])
  const isFullyLoaded = ref(false)
  let currentLoadId = 0

  const schedule = (cb: () => void) => {
    if ('requestIdleCallback' in window) {
      ;(window as any).requestIdleCallback(cb)
    } else {
      requestAnimationFrame(cb)
    }
  }

  const resetChunks = () => {
    chunkedItems.value = []
    isFullyLoaded.value = false
  }

  const startChunkLoad = (items: T[]) => {
    const loadId = ++currentLoadId
    resetChunks()

    if (!items?.length) {
      isFullyLoaded.value = true
      return
    }

    const total = items.length
    let index = 0

    const pushNextChunk = async () => {
      if (loadId !== currentLoadId) return

      const nextChunk = items.slice(index, index + chunkSize)
      if (nextChunk.length) {
        await nextTick()
        chunkedItems.value = [...chunkedItems.value, ...nextChunk]

        index += chunkSize
        if (index < total) {
          schedule(pushNextChunk)
        } else {
          isFullyLoaded.value = true
        }
      }
    }

    schedule(pushNextChunk)
  }

  watch(
    source,
    debounce((newItems: T[]) => startChunkLoad(newItems), delay),
    { immediate: true },
  )

  return { chunkedItems, isFullyLoaded, resetChunks }
}
